import { supabaseClient } from "@/lib/database/supabase";
import isTruthy from "@/lib/utils/boolean/isTruthy";
import { QueryData } from "@supabase/supabase-js";
import { addWeeks, endOfDay, endOfWeek, isAfter, isBefore, isFuture, isPast, isSameDay, parseISO, startOfDay, startOfWeek, subWeeks } from "date-fns";
import { defineStore } from "pinia";
import { computed, ref } from "vue";

const runForTimelineSelect = `
  id, 
  start_at, 
  end_at, 
  canceled_at, 
  min_player_count, 
  max_player_count, 
  rsvp_at,
  season_id,
  court:courts(id, name, timezone, photo_blurhash, photo_storage_path),
  crews(id, name, location, players(id))
`;

const runForCarouselSelect = `
  ${runForTimelineSelect},
  statuses(run_id, player_id, probability, note, created_at, updated_at, player:players(id, name, theme, avatar_storage_path)),
  sub_requests(id, run_id, player_id, accepted_at),
  season:seasons(id, players(id)),
  awards:run_awards(*),
  reviews:run_reviews(*, award_votes(*)),
  award_winners:run_award_winners(*, award:awards(*))
`;

function createRunsForCarouselQuery () {
  return supabaseClient.from("runs")
    .select(runForCarouselSelect);
}

function createRunsForTimelineQuery () {
  return supabaseClient.from("runs")
    .select(runForTimelineSelect);
}

export type RunForCarousel = QueryData<ReturnType<typeof createRunsForCarouselQuery>>[0];
export type RunForTimeline = QueryData<ReturnType<typeof createRunsForTimelineQuery>>[0];

export default defineStore("RunCarouselStore", () => {
  const runMap = ref<Map<string, RunForTimeline | null>>(new Map());

  const allRuns = computed(() => {
    return [...runMap.value.values()].filter(isTruthy);
  });

  const currentRuns = computed(() => {
    const today = new Date();

    const filteredRuns = allRuns.value
      .filter(isRunForCarousel)
      .filter((run) => {
        return isAfter(run.start_at, subWeeks(startOfWeek(today), 1))
          && isBefore(run.start_at, addWeeks(endOfWeek(today), 1));
      })
      .sort((a: RunForTimeline, b: RunForTimeline) => {
        const startDifference = +parseISO(a.start_at) - +parseISO(b.start_at);
        if (startDifference !== 0) return startDifference;

        return +parseISO(a.end_at || a.start_at) - +parseISO(b.end_at || b.start_at);
      });

    return [
      ...filteredRuns.filter((run) => isBefore(run.start_at, startOfDay(today))).slice(-3),
      ...filteredRuns.filter((run) => isSameDay(run.start_at, today)),
      ...filteredRuns.filter((run) => isAfter(run.start_at, endOfDay(today))).slice(0, 3),
    ];
  });

  const currentRunIds = computed(() => currentRuns.value.map((run) => run.id));

  const pastRuns = computed(() => {
    return allRuns.value
      .filter((run) => !currentRunIds.value.includes(run.id))
      .filter((run) => isPast(run.start_at));
  });

  const futureRuns = computed(() => {
    return allRuns.value
      .filter((run) => !currentRunIds.value.includes(run.id))
      .filter((run) => isFuture(run.start_at));
  });

  function isRunForCarousel (run: RunForTimeline): run is RunForCarousel {
    return "statuses" in run;
  }

  async function fetchRun (id: string) {
    const {
      data: run,
      error,
    } = await createRunsForCarouselQuery()
      .eq("id", id)
      .maybeSingle();

    if (error) return [error, null];
    if (run) runMap.value.set(id, run);

    return [null, run];
  }

  async function fetchCurrentRuns () {
    const today = new Date();

    const {
      data: runs,
      error,
    } = await createRunsForCarouselQuery()
      .gt("start_at", subWeeks(startOfWeek(today), 1).toISOString())
      .lt("start_at", addWeeks(endOfWeek(today), 1).toISOString())
      .order("start_at", {
        ascending: true,
      })
      .order("end_at", {
        ascending: true,
      })
      .retry(import.meta.env.MODE === "production");

    if (error) return [error, null];

    runs.forEach((run) => {
      runMap.value.set(run.id, run);
    });

    return [null, runs];
  }

  async function fetchPastRuns () {
    const {
      data: pastRuns,
      error,
    } = await createRunsForTimelineQuery()
      .lte("start_at", new Date().toISOString())
      .order("start_at", {
        ascending: false,
      })
      .order("end_at", {
        ascending: false,
      })
      .limit(50);
    // TODO: infinite scroll

    if (error) return [error, null];

    pastRuns.forEach(upsertRun);

    return [null, pastRuns];
  }

  async function fetchFutureRuns () {
    const {
      data: futureRuns,
      error,
    } = await createRunsForTimelineQuery()
      .gte("start_at", new Date().toISOString())
      .order("start_at", {
        ascending: true,
      })
      .order("end_at", {
        ascending: true,
      })
      .limit(50);
    // TODO: infinite scroll

    if (error) return [error, null];

    futureRuns.forEach(upsertRun);

    return [null, futureRuns];
  }

  function upsertRun (run: RunForTimeline) {
    if (runMap.value.has(run.id)) {
      return updateRun(run.id, run);
    } else {
      runMap.value.set(run.id, run);
    }

    return run;
  }

  function updateRun (id: string, run: Partial<RunForTimeline>) {
    const existingRun = runMap.value.get(id);
    if (!existingRun) return;

    const updatedRun = {
      ...existingRun,
      ...run,
    };
    runMap.value.set(id, updatedRun);

    return updatedRun;
  }

  function removeRun (id: string) {
    runMap.value.delete(id);
  }

  return {
    currentRuns,
    pastRuns,
    futureRuns,
    fetchCurrentRuns,
    fetchPastRuns,
    fetchFutureRuns,
    fetchRun,
    updateRun,
    removeRun,
  };
});
